import { useEffect, type ReactNode } from "react";
import { brandColors as colors, usePalette } from "@/lib/palette";
import { fonts } from "@/lib/fonts";
import { renderMarkdown } from "@/lib/markdown";

const STYLE_ID = "guide-body-style";

const STYLE_TEXT = `
  .guide-body { font-weight: 500; letter-spacing: 0.02em; line-height: 1.75; }
  .guide-body p { margin: 0 0 1.1rem; font-size: 1.0625rem; }
  .guide-body h2, .guide-body h3, .guide-body h4 {
    font-family: ${fonts.logo};
    color: var(--guide-navy);
    font-weight: 400;
    line-height: 1.2;
    margin: 2.25rem 0 0.85rem;
  }
  .guide-body h2 { font-size: 1.75rem; }
  .guide-body h3 { font-size: 1.4rem; }
  .guide-body h4 {
    font-size: 1.15rem;
    font-family: ${fonts.body};
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: var(--guide-accent);
  }
  .guide-body ul, .guide-body ol { margin: 0 0 1.25rem; padding-left: 1.25rem; }
  .guide-body li { margin-bottom: 0.5rem; }
  .guide-body ol { list-style: none; counter-reset: step; padding-left: 0; }
  .guide-body ol > li {
    counter-increment: step;
    position: relative;
    padding: 0.85rem 1rem 0.85rem 3.25rem;
    margin: 0 0 0.75rem;
    background: white;
    border: 1px solid rgba(201,147,76,0.18);
    border-radius: 14px;
  }
  .guide-body ol > li::before {
    content: counter(step);
    position: absolute;
    left: 0.9rem;
    top: 0.85rem;
    width: 1.85rem;
    height: 1.85rem;
    border-radius: 999px;
    background: var(--guide-accent);
    color: white;
    font-family: ${fonts.body};
    font-weight: 700;
    font-size: 0.85rem;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .guide-body ul > li { padding-left: 0.25rem; }
  .guide-body a {
    color: var(--guide-accent);
    text-decoration: underline;
    text-underline-offset: 3px;
    font-weight: 600;
  }
  .guide-body a:hover { color: var(--guide-warm); }
  .guide-body strong { color: var(--guide-navy); }
  .guide-body blockquote {
    margin: 1.5rem 0;
    padding: 1rem 1.25rem;
    background: ${colors.blushLight};
    border-left: 4px solid var(--guide-accent);
    border-radius: 0 12px 12px 0;
    font-style: italic;
    color: ${colors.navy};
  }
`;

function ensureStyleInjected() {
  if (typeof document === "undefined") return;
  if (document.getElementById(STYLE_ID)) return;
  const el = document.createElement("style");
  el.id = STYLE_ID;
  el.textContent = STYLE_TEXT;
  document.head.appendChild(el);
}

export default function MarkdownBody({ children }: { children: string }) {
  const theme = usePalette();
  useEffect(() => {
    ensureStyleInjected();
  }, []);
  const nodes: ReactNode[] = renderMarkdown(children);
  return (
    <div
      className="guide-body"
      style={
        {
          fontFamily: fonts.body,
          color: `${colors.navy}d9`,
          ["--guide-accent" as string]: theme.accent,
          ["--guide-warm" as string]: theme.warm,
          ["--guide-navy" as string]: colors.navy,
        } as React.CSSProperties
      }
    >
      {nodes}
    </div>
  );
}
